import { useQuery, useQueryClient } from 'react-query';
import { wordpressApi } from '../services/wordpressApi';
import { FEATURED_TAG_SLUGS } from '../config/featured';

export const useWordPress = () => {
  const queryClient = useQueryClient();

  // Get posts with pagination and filtering
  const usePosts = (page = 1, perPage = 6, category = 'all', tag = 'all') => {
    return useQuery(
      ['posts', page, perPage, category, tag],
      () => wordpressApi.getPosts(page, perPage, category, tag),
      {
        keepPreviousData: true,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
      }
    );
  };

  // Get a single post by slug
  const usePost = (slug) => {
    return useQuery(
      ['post', slug],
      () => wordpressApi.getPostBySlug(slug),
      {
        enabled: !!slug,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
      }
    );
  };

  // Get categories
  const useCategories = () => {
    return useQuery(
      ['categories'],
      () => wordpressApi.getCategories(),
      {
        staleTime: 30 * 60 * 1000, // 30 minutes
        cacheTime: 60 * 60 * 1000, // 1 hour
      }
    );
  };

  // Get tags
  const useTags = () => {
    return useQuery(
      ['tags'],
      () => wordpressApi.getTags(),
      {
        staleTime: 30 * 60 * 1000, // 30 minutes
        cacheTime: 60 * 60 * 1000, // 1 hour
      }
    );
  };

  // Search posts
  const useSearchPosts = (searchTerm, page = 1, perPage = 6) => {
    return useQuery(
      ['search', searchTerm, page, perPage],
      () => wordpressApi.searchPosts(searchTerm, page, perPage),
      {
        enabled: !!searchTerm && searchTerm.length > 2,
        keepPreviousData: true,
        staleTime: 2 * 60 * 1000, // 2 minutes
        cacheTime: 5 * 60 * 1000, // 5 minutes
      }
    );
  };

  // Get recent posts for homepage
  const useRecentPosts = (limit = 3) => {
    return useQuery(
      ['recentPosts', limit],
      () => wordpressApi.getPosts(1, limit),
      {
        staleTime: 10 * 60 * 1000, // 10 minutes
        cacheTime: 20 * 60 * 1000, // 20 minutes
      }
    );
  };

  // Resolve the featured-carousel tag slugs to tag IDs
  const useFeaturedTags = () => {
    return useQuery(
      ['featuredTags', FEATURED_TAG_SLUGS],
      () => wordpressApi.getTagsBySlugs(FEATURED_TAG_SLUGS),
      {
        staleTime: 30 * 60 * 1000, // 30 minutes
        cacheTime: 60 * 60 * 1000, // 1 hour
      }
    );
  };

  // Get the starred posts for the featured carousel (dependent on useFeaturedTags)
  const useFeaturedPosts = (tagIds = []) => {
    return useQuery(
      ['featuredPosts', tagIds],
      () => wordpressApi.getPostsByTagIds(tagIds),
      {
        enabled: tagIds.length > 0,
        staleTime: 10 * 60 * 1000, // 10 minutes
        cacheTime: 20 * 60 * 1000, // 20 minutes
      }
    );
  };

  // Get all posts (lightweight) for dive site map linking
  const usePostsForMap = () => {
    return useQuery(
      ['postsForMap'],
      () => wordpressApi.getPostsForMap(),
      {
        staleTime: 30 * 60 * 1000, // 30 minutes
        cacheTime: 60 * 60 * 1000, // 1 hour
      }
    );
  };

  // Invalidate posts cache
  const invalidatePosts = () => {
    queryClient.invalidateQueries(['posts']);
    queryClient.invalidateQueries(['recentPosts']);
    queryClient.invalidateQueries(['featuredPosts']);
  };

  // Invalidate search cache
  const invalidateSearch = () => {
    queryClient.invalidateQueries(['search']);
  };

  return {
    usePosts,
    usePost,
    useCategories,
    useTags,
    useSearchPosts,
    useRecentPosts,
    useFeaturedTags,
    useFeaturedPosts,
    usePostsForMap,
    invalidatePosts,
    invalidateSearch,
  };
}; 